import { $ctx, $view } from '@milkdown/utils';
import { codeBlockSchema } from '@milkdown/preset-commonmark';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, drawSelection, keymap } from '@codemirror/view';
import { exitCode } from '@milkdown/prose/commands';
import { undo, redo } from '@milkdown/prose/history';
import { TextSelection } from '@milkdown/prose/state';
import { h, Fragment, defineComponent, ref, watch, computed, onMounted, onUnmounted, watchEffect, createApp } from 'vue';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { computePosition } from '@floating-ui/dom';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
function withMeta(plugin, meta) {
  Object.assign(plugin, {
    meta: __spreadValues({
      package: "@milkdown/components"
    }, meta)
  });
  return plugin;
}

const defaultConfig = {
  extensions: [],
  languages: [],
  expandIcon: "\u2B07",
  searchIcon: "\u{1F50D}",
  clearSearchIcon: "\u232B",
  searchPlaceholder: "Search language",
  noResultText: "No result",
  copyText: "Copy",
  copyIcon: "\u{1F4CB}",
  onCopy: () => {
  },
  renderLanguage: (language) => language,
  renderPreview: () => null,
  previewToggleButton: (previewOnlyMode) => previewOnlyMode ? "Edit" : "Hide",
  previewLabel: "Preview",
  previewLoading: "Loading..."
};
const codeBlockConfig = $ctx(defaultConfig, "codeBlockConfigCtx");
withMeta(codeBlockConfig, {
  displayName: "Config<code-block>",
  group: "CodeBlock"
});

class LanguageLoader {
  constructor(languages) {
    this.languages = languages;
    this.map = {};
    languages.forEach((language) => {
      language.alias.forEach((alias) => {
        this.map[alias] = language;
      });
    });
  }
  getAll() {
    return this.languages.map((language) => {
      return {
        name: language.name,
        alias: language.alias
      };
    });
  }
  load(languageName) {
    const languageMap = this.map;
    const language = languageMap[languageName.toLowerCase()];
    if (!language) return Promise.resolve(void 0);
    if (language.support) return Promise.resolve(language.support);
    return language.load();
  }
}

function keepAlive(..._args) {
}

keepAlive(h);
function Icon({ icon, class: className, onClick }) {
  return /* @__PURE__ */ h(
    "span",
    {
      class: clsx("milkdown-icon", className),
      onPointerdown: onClick,
      innerHTML: icon ? DOMPurify.sanitize(icon.trim()) : void 0
    }
  );
}
Icon.props = {
  icon: {
    type: String,
    required: false
  },
  class: {
    type: String,
    required: false
  },
  onClick: {
    type: Function,
    required: false
  }
};

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
keepAlive(h, Fragment);
function copyToClipboard(text) {
  return __async(this, null, function* () {
    try {
      return navigator.clipboard.writeText(text);
    } catch (e) {
      const element = document.createElement("textarea");
      const previouslyFocusedElement = document.activeElement;
      element.value = text;
      element.setAttribute("readonly", "");
      element.style.contain = "strict";
      element.style.position = "absolute";
      element.style.left = "-9999px";
      element.style.fontSize = "12pt";
      const selection = document.getSelection();
      const originalRange = selection ? selection.rangeCount > 0 && selection.getRangeAt(0) : null;
      document.body.appendChild(element);
      element.select();
      element.selectionStart = 0;
      element.selectionEnd = text.length;
      document.execCommand("copy");
      document.body.removeChild(element);
      if (originalRange) {
        selection.removeAllRanges();
        selection.addRange(originalRange);
      }
      if (previouslyFocusedElement) {
        previouslyFocusedElement.focus();
      }
    }
  });
}
const CopyButton = defineComponent({
  props: {
    copyText: {
      type: String,
      required: true
    },
    copyIcon: {
      type: String,
      required: true
    },
    onCopy: {
      type: Function,
      required: true
    },
    text: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const onCopyCode = () => {
      copyToClipboard(props.text).then(() => props.onCopy(props.text)).catch(console.error);
    };
    return () => {
      return /* @__PURE__ */ h(Fragment, null, /* @__PURE__ */ h("button", { type: "button", class: "copy-button", onClick: onCopyCode }, /* @__PURE__ */ h(Icon, { icon: props.copyIcon }), props.copyText));
    };
  }
});

keepAlive(h, Fragment);
const LanguagePicker = defineComponent({
  props: {
    language: {
      type: Object,
      required: true
    },
    getReadOnly: {
      type: Function,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    getAllLanguages: {
      type: Function,
      required: true
    },
    setLanguage: {
      type: Function,
      required: true
    }
  },
  setup({ language, config, setLanguage, getAllLanguages, getReadOnly }) {
    const triggerRef = ref();
    const showPicker = ref(false);
    const searchRef = ref();
    const pickerRef = ref();
    const filter = ref("");
    watch([showPicker, triggerRef, pickerRef], () => {
      filter.value = "";
      const picker = triggerRef.value;
      const languageList = pickerRef.value;
      if (!picker || !languageList) return;
      computePosition(picker, languageList, {
        placement: "bottom-start"
      }).then(({ x, y }) => {
        Object.assign(languageList.style, {
          left: `${x}px`,
          top: `${y}px`
        });
      }).catch(console.error);
    });
    const onTogglePicker = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (getReadOnly()) return;
      const next = !showPicker.value;
      showPicker.value = next;
      if (next) {
        setTimeout(() => {
          var _a;
          return (_a = searchRef.value) == null ? void 0 : _a.focus();
        }, 0);
      }
    };
    const changeFilter = (e) => {
      const target = e.target;
      filter.value = target.value;
    };
    const onSearchKeydown = (e) => {
      if (e.key === "Escape") filter.value = "";
    };
    const languages = computed(() => {
      var _a;
      if (!showPicker.value) return [];
      const all = (_a = getAllLanguages()) != null ? _a : [];
      const selected = all.find(
        (languageInfo) => languageInfo.name.toLowerCase() === language.value.toLowerCase()
      );
      const filtered = all.filter((languageInfo) => {
        const currentValue = filter.value.toLowerCase();
        return (languageInfo.name.toLowerCase().includes(currentValue) || languageInfo.alias.some(
          (alias) => alias.toLowerCase().includes(currentValue)
        )) && languageInfo !== selected;
      });
      if (filtered.length === 0) return [];
      if (!selected) return filtered;
      return [selected, ...filtered];
    });
    const clickHandler = (e) => {
      const target = e.target;
      if (triggerRef.value && triggerRef.value.contains(target)) return;
      const picker = pickerRef.value;
      const trigger = triggerRef.value;
      if (!trigger || !picker) return;
      if (trigger.dataset.expanded !== "true") return;
      if (!picker.contains(target)) showPicker.value = false;
    };
    onMounted(() => {
      window.addEventListener("click", clickHandler);
    });
    onUnmounted(() => {
      window.removeEventListener("click", clickHandler);
    });
    return () => {
      return /* @__PURE__ */ h(Fragment, null, /* @__PURE__ */ h(
        "button",
        {
          type: "button",
          ref: triggerRef,
          class: "language-button",
          onClick: onTogglePicker,
          "data-expanded": String(showPicker.value)
        },
        language.value || "Text",
        /* @__PURE__ */ h("div", { class: "expand-icon" }, /* @__PURE__ */ h(Icon, { icon: config.expandIcon }))
      ), /* @__PURE__ */ h("div", { ref: pickerRef, class: "language-picker" }, showPicker.value ? /* @__PURE__ */ h("div", { class: "list-wrapper" }, /* @__PURE__ */ h("div", { class: "search-box" }, /* @__PURE__ */ h("div", { class: "search-icon" }, /* @__PURE__ */ h(Icon, { icon: config.searchIcon })), /* @__PURE__ */ h(
        "input",
        {
          ref: searchRef,
          class: "search-input",
          placeholder: config.searchPlaceholder,
          value: filter.value,
          onInput: changeFilter,
          onKeydown: onSearchKeydown
        }
      ), /* @__PURE__ */ h(
        "div",
        {
          class: clsx(
            "clear-icon",
            filter.value.length === 0 && "hidden"
          ),
          onMousedown: (e) => {
            e.preventDefault();
            filter.value = "";
          }
        },
        /* @__PURE__ */ h(Icon, { icon: config.clearSearchIcon })
      )), /* @__PURE__ */ h(
        "ul",
        {
          class: "language-list",
          role: "listbox",
          onKeydown: (e) => {
            if (e.key === "Enter") {
              const active = document.activeElement;
              if (active instanceof HTMLElement && active.dataset.language)
                setLanguage(active.dataset.language);
            }
          }
        },
        !languages.value.length ? /* @__PURE__ */ h("li", { class: "language-list-item no-result" }, config.noResultText) : languages.value.map((languageInfo) => /* @__PURE__ */ h(
          "li",
          {
            role: "listitem",
            tabindex: "0",
            class: "language-list-item",
            "aria-selected": languageInfo.name.toLowerCase() === language.value.toLowerCase(),
            "data-language": languageInfo.name,
            onClick: () => {
              setLanguage(languageInfo.name);
              showPicker.value = false;
            }
          },
          config.renderLanguage(
            languageInfo.name,
            languageInfo.name.toLowerCase() === language.value.toLowerCase()
          )
        ))
      )) : null));
    };
  }
});

keepAlive(h, Fragment);
const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
function createSvgAwareSanitizer() {
  const purify = DOMPurify();
  const config = {
    ADD_TAGS: ["foreignObject"],
    ADD_ATTR: ["xmlns"],
    HTML_INTEGRATION_POINTS: { foreignobject: true }
  };
  purify.addHook("uponSanitizeElement", (node, data) => {
    var _a;
    if (data.tagName === "foreignobject") {
      const parent = node.parentElement;
      if (!parent || parent.namespaceURI !== SVG_NAMESPACE) {
        (_a = node.parentNode) == null ? void 0 : _a.removeChild(node);
      }
    }
  });
  return (dirty) => purify.sanitize(dirty, config);
}
let cachedSanitizer;
function sanitizeSvg(dirty) {
  cachedSanitizer != null ? cachedSanitizer : cachedSanitizer = createSvgAwareSanitizer();
  return cachedSanitizer(dirty);
}
const PreviewPanel = defineComponent({
  props: {
    text: {
      type: Object,
      required: true
    },
    language: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    previewOnlyMode: {
      type: Object,
      required: true
    },
    preview: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { previewOnlyMode, config, preview } = props;
    const previewRef = ref();
    watchEffect(() => {
      const previewContainer = previewRef.value;
      if (!previewContainer) return;
      while (previewContainer.firstChild) {
        previewContainer.removeChild(previewContainer.firstChild);
      }
      const previewContent = preview.value;
      if (typeof previewContent === "string" || previewContent instanceof Element) {
        previewContainer.innerHTML = sanitizeSvg(previewContent);
      }
    });
    return () => {
      if (!preview.value) return null;
      return /* @__PURE__ */ h("div", { class: "preview-panel" }, !previewOnlyMode.value && /* @__PURE__ */ h(Fragment, null, /* @__PURE__ */ h("div", { class: "preview-divider" }), /* @__PURE__ */ h("div", { class: "preview-label" }, config.previewLabel)), /* @__PURE__ */ h("div", { ref: previewRef, class: "preview" }));
    };
  }
});

keepAlive(h, Fragment);
const CodeBlock = defineComponent({
  props: {
    text: {
      type: Object,
      required: true
    },
    selected: {
      type: Object,
      required: true
    },
    getReadOnly: {
      type: Function,
      required: true
    },
    codemirror: {
      type: Object,
      required: true
    },
    language: {
      type: Object,
      required: true
    },
    getAllLanguages: {
      type: Function,
      required: true
    },
    setLanguage: {
      type: Function,
      required: true
    },
    config: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    var _a;
    const previewOnlyByDefault = (_a = props.config.previewOnlyByDefault) != null ? _a : props.getReadOnly();
    const previewOnlyMode = ref(previewOnlyByDefault);
    const codemirrorHostRef = ref();
    const preview = ref(null);
    onMounted(() => {
      var _a2;
      while ((_a2 = codemirrorHostRef.value) == null ? void 0 : _a2.firstChild) {
        codemirrorHostRef.value.removeChild(codemirrorHostRef.value.firstChild);
      }
      if (codemirrorHostRef.value) {
        codemirrorHostRef.value.appendChild(props.codemirror.dom);
      }
    });
    watch(
      () => [props.text.value, props.language.value],
      () => {
        const result = props.config.renderPreview(
          props.language.value,
          props.text.value,
          (value) => preview.value = value
        );
        if (result) {
          preview.value = result;
        }
        const isAsyncPreview = result === void 0;
        if (isAsyncPreview && !preview.value) {
          preview.value = DOMPurify.sanitize(props.config.previewLoading);
        }
        if (result === null) {
          preview.value = null;
        }
      },
      { immediate: true }
    );
    const empty = () => {
    };
    return () => {
      var _a2;
      return /* @__PURE__ */ h(Fragment, null, /* @__PURE__ */ h("div", { class: "tools" }, /* @__PURE__ */ h(
        LanguagePicker,
        {
          language: props.language,
          config: props.config,
          setLanguage: props.setLanguage,
          getAllLanguages: props.getAllLanguages,
          getReadOnly: props.getReadOnly
        }
      ), /* @__PURE__ */ h("div", { class: "tools-button-group" }, /* @__PURE__ */ h(
        CopyButton,
        {
          copyIcon: props.config.copyIcon,
          copyText: props.config.copyText,
          onCopy: (_a2 = props.config.onCopy) != null ? _a2 : empty,
          text: props.text.value
        }
      ), preview.value ? /* @__PURE__ */ h(
        "button",
        {
          class: "preview-toggle-button",
          onClick: () => previewOnlyMode.value = !previewOnlyMode.value
        },
        /* @__PURE__ */ h(
          Icon,
          {
            icon: props.config.previewToggleButton(
              previewOnlyMode.value
            )
          }
        )
      ) : null)), /* @__PURE__ */ h(
        "div",
        {
          ref: codemirrorHostRef,
          class: clsx(
            "codemirror-host",
            preview.value && previewOnlyMode.value && "hidden"
          )
        }
      ), /* @__PURE__ */ h(
        PreviewPanel,
        {
          text: props.text,
          language: props.language,
          config: props.config,
          previewOnlyMode,
          preview
        }
      ));
    };
  }
});

const visibilityCallbacks = /* @__PURE__ */ new WeakMap();
let sharedObserver = null;
function getSharedObserver() {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const callback = visibilityCallbacks.get(entry.target);
          callback == null ? void 0 : callback(entry.isIntersecting);
        }
      },
      { rootMargin: "200px" }
    );
  }
  return sharedObserver;
}
const _CodeMirrorBlock = class _CodeMirrorBlock {
  constructor(node, view, getPos, loader, config) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;
    this.loader = loader;
    this.config = config;
    this.selected = ref(false);
    this.language = ref("");
    this.text = ref("");
    this.initialized = false;
    this.updating = false;
    this.languageName = "";
    this.teardownTimer = null;
    this.forwardUpdate = (update) => {
      var _a;
      if (this.updating || !this.cm.hasFocus) return;
      let offset = ((_a = this.getPos()) != null ? _a : 0) + 1;
      const { main } = update.state.selection;
      const selFrom = offset + main.from;
      const selTo = offset + main.to;
      const pmSel = this.view.state.selection;
      if (update.docChanged || pmSel.from !== selFrom || pmSel.to !== selTo) {
        const tr = this.view.state.tr;
        update.changes.iterChanges((fromA, toA, fromB, toB, text) => {
          if (text.length)
            tr.replaceWith(
              offset + fromA,
              offset + toA,
              this.view.state.schema.text(text.toString())
            );
          else tr.delete(offset + fromA, offset + toA);
          offset += toB - fromB - (toA - fromA);
        });
        tr.setSelection(TextSelection.create(tr.doc, selFrom, selTo));
        this.view.dispatch(tr);
      }
    };
    this.createApp = () => {
      return createApp(CodeBlock, {
        text: this.text,
        selected: this.selected,
        codemirror: this.cm,
        language: this.language,
        getAllLanguages: this.getAllLanguages,
        getReadOnly: () => !this.view.editable,
        setLanguage: this.setLanguage,
        config: this.config
      });
    };
    this.codeMirrorKeymap = () => {
      const view = this.view;
      return [
        { key: "ArrowUp", run: () => this.maybeEscape("line", -1) },
        { key: "ArrowLeft", run: () => this.maybeEscape("char", -1) },
        { key: "ArrowDown", run: () => this.maybeEscape("line", 1) },
        { key: "ArrowRight", run: () => this.maybeEscape("char", 1) },
        {
          key: "Mod-Enter",
          run: () => {
            if (!exitCode(view.state, view.dispatch)) return false;
            view.focus();
            return true;
          }
        },
        { key: "Mod-z", run: () => undo(view.state, view.dispatch) },
        { key: "Shift-Mod-z", run: () => redo(view.state, view.dispatch) },
        { key: "Mod-y", run: () => redo(view.state, view.dispatch) },
        {
          key: "Backspace",
          run: () => {
            var _a;
            const ranges = this.cm.state.selection.ranges;
            if (ranges.length > 1) return false;
            const selection = ranges[0];
            if (selection && (!selection.empty || selection.anchor > 0))
              return false;
            if (this.cm.state.doc.lines >= 2) return false;
            const state = this.view.state;
            const pos = (_a = this.getPos()) != null ? _a : 0;
            const tr = state.tr.replaceWith(
              pos,
              pos + this.node.nodeSize,
              state.schema.nodes.paragraph.createChecked({}, this.node.content)
            );
            tr.setSelection(TextSelection.near(tr.doc.resolve(pos)));
            this.view.dispatch(tr);
            this.view.focus();
            return true;
          }
        }
      ];
    };
    this.maybeEscape = (unit, dir) => {
      var _a;
      const { state } = this.cm;
      let main = state.selection.main;
      if (!main.empty) return false;
      if (unit === "line") main = state.doc.lineAt(main.head);
      if (dir < 0 ? main.from > 0 : main.to < state.doc.length) return false;
      const targetPos = ((_a = this.getPos()) != null ? _a : 0) + (dir < 0 ? 0 : this.node.nodeSize);
      const selection = TextSelection.near(
        this.view.state.doc.resolve(targetPos),
        dir
      );
      const tr = this.view.state.tr.setSelection(selection).scrollIntoView();
      this.view.dispatch(tr);
      this.view.focus();
      return true;
    };
    this.setLanguage = (language) => {
      var _a;
      this.view.dispatch(
        this.view.state.tr.setNodeAttribute(
          (_a = this.getPos()) != null ? _a : 0,
          "language",
          language
        )
      );
    };
    this.getAllLanguages = () => {
      return this.loader.getAll();
    };
    var _a;
    this.languageConf = new Compartment();
    this.readOnlyConf = new Compartment();
    this.text.value = this.node.textContent;
    this.language.value = (_a = this.node.attrs.language) != null ? _a : "";
    this.dom = document.createElement("div");
    this.dom.className = "milkdown-code-block";
    this.disposeSelectedWatcher = watchEffect(() => {
      const isSelected = this.selected.value;
      if (isSelected) {
        this.dom.classList.add("selected");
      } else {
        this.dom.classList.remove("selected");
      }
    });
    this.renderPlaceholder();
    visibilityCallbacks.set(this.dom, (isIntersecting) => {
      if (isIntersecting) {
        this.cancelTeardown();
        this.initializeCodeMirror();
      } else if (this.initialized) {
        this.scheduleTeardown();
      }
    });
    getSharedObserver().observe(this.dom);
  }
  renderPlaceholder() {
    const pre = document.createElement("pre");
    pre.className = "milkdown-code-block-placeholder";
    const code = document.createElement("code");
    code.textContent = this.node.textContent;
    pre.appendChild(code);
    this.dom.appendChild(pre);
  }
  initializeCodeMirror() {
    if (this.initialized) return;
    this.initialized = true;
    this.cm = new EditorView({
      doc: this.node.textContent,
      root: this.view.root,
      extensions: [
        this.readOnlyConf.of(EditorState.readOnly.of(!this.view.editable)),
        drawSelection(),
        keymap.of(this.codeMirrorKeymap()),
        this.languageConf.of([]),
        EditorState.changeFilter.of(() => this.view.editable),
        ...this.config.extensions,
        EditorView.updateListener.of(this.forwardUpdate)
      ]
    });
    const placeholder = this.dom.querySelector(
      ".milkdown-code-block-placeholder"
    );
    if (placeholder) {
      this.dom.removeChild(placeholder);
    }
    this.app = this.createApp();
    this.app.mount(this.dom);
    this.updateLanguage();
  }
  teardownCodeMirror() {
    if (!this.initialized) return;
    if (this.cm.hasFocus || this.selected.value) return;
    this.app.unmount();
    this.cm.destroy();
    this.initialized = false;
    this.languageName = "";
    while (this.dom.firstChild) {
      this.dom.removeChild(this.dom.firstChild);
    }
    this.renderPlaceholder();
  }
  scheduleTeardown() {
    this.cancelTeardown();
    this.teardownTimer = setTimeout(
      () => this.teardownCodeMirror(),
      _CodeMirrorBlock.TEARDOWN_DELAY
    );
  }
  cancelTeardown() {
    if (this.teardownTimer != null) {
      clearTimeout(this.teardownTimer);
      this.teardownTimer = null;
    }
  }
  updateLanguage() {
    const languageName = this.node.attrs.language;
    if (languageName === this.languageName) return;
    this.language.value = languageName;
    const language = this.loader.load(languageName != null ? languageName : "");
    language.then((lang) => {
      if (lang) {
        this.cm.dispatch({
          effects: this.languageConf.reconfigure(lang)
        });
        this.languageName = languageName;
      }
    }).catch(console.error);
  }
  setSelection(anchor, head) {
    if (!this.initialized) {
      this.initializeCodeMirror();
    }
    if (!this.cm.dom.isConnected) return;
    this.cm.focus();
    this.updating = true;
    this.cm.dispatch({ selection: { anchor, head } });
    this.updating = false;
  }
  update(node) {
    var _a;
    if (node.type !== this.node.type) return false;
    if (this.updating) return true;
    this.node = node;
    this.text.value = node.textContent;
    this.language.value = (_a = node.attrs.language) != null ? _a : "";
    if (!this.initialized) {
      const code = this.dom.querySelector(
        ".milkdown-code-block-placeholder code"
      );
      if (code) {
        code.textContent = node.textContent;
      }
      return true;
    }
    this.updateLanguage();
    if (this.view.editable === this.cm.state.readOnly) {
      this.cm.dispatch({
        effects: this.readOnlyConf.reconfigure(
          EditorState.readOnly.of(!this.view.editable)
        )
      });
    }
    const change = computeChange(this.cm.state.doc.toString(), node.textContent);
    if (change) {
      this.updating = true;
      this.cm.dispatch({
        changes: { from: change.from, to: change.to, insert: change.text },
        scrollIntoView: true
      });
      this.updating = false;
    }
    return true;
  }
  selectNode() {
    if (!this.initialized) {
      this.initializeCodeMirror();
    }
    this.selected.value = true;
    this.cm.focus();
  }
  deselectNode() {
    this.selected.value = false;
  }
  stopEvent() {
    return true;
  }
  destroy() {
    this.cancelTeardown();
    getSharedObserver().unobserve(this.dom);
    visibilityCallbacks.delete(this.dom);
    if (this.initialized) {
      this.app.unmount();
      this.cm.destroy();
    }
    this.disposeSelectedWatcher();
  }
};
/** Delay before tearing down an off-screen CodeMirror instance. */
_CodeMirrorBlock.TEARDOWN_DELAY = 5e3;
let CodeMirrorBlock = _CodeMirrorBlock;
function computeChange(oldVal, newVal) {
  if (oldVal === newVal) return null;
  let start = 0;
  let oldEnd = oldVal.length;
  let newEnd = newVal.length;
  while (start < oldEnd && oldVal.charCodeAt(start) === newVal.charCodeAt(start))
    ++start;
  while (oldEnd > start && newEnd > start && oldVal.charCodeAt(oldEnd - 1) === newVal.charCodeAt(newEnd - 1)) {
    oldEnd--;
    newEnd--;
  }
  return { from: start, to: oldEnd, text: newVal.slice(start, newEnd) };
}

const codeBlockView = $view(
  codeBlockSchema.node,
  (ctx) => {
    const config = ctx.get(codeBlockConfig.key);
    const languageLoader = new LanguageLoader(config.languages);
    return (node, view, getPos) => new CodeMirrorBlock(node, view, getPos, languageLoader, config);
  }
);
withMeta(codeBlockView, {
  displayName: "NodeView<code-block>",
  group: "CodeBlock"
});

const codeBlockComponent = [
  codeBlockView,
  codeBlockConfig
];

export { CodeMirrorBlock, codeBlockComponent, codeBlockConfig, codeBlockView, defaultConfig };
//# sourceMappingURL=index.js.map
